import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useRefreshFiles } from "@/hooks/use-knowledge-base";
import {
  failureMessage,
  type KnowledgeBaseFile,
  type KnowledgeBaseUsage,
  sha256Hex,
  uploadFile,
} from "@/services/knowledge-base.service";
import { UPLOAD_FAILED } from "@/lib/resumable-upload";
import { ACCEPTED_EXTENSIONS } from "@/utils/kb-file-types";
import { checkFiles, fileCount, fileLimits, formatBytes, needsHash, uploadRoom } from "@/utils/kb-upload-rules";

/** A picked file whose bytes the browser could not read. */
const UNREADABLE = "This file could not be read. Pick it again.";

/** The docs page on laying out a knowledge base so the agent finds the right passage. */
const WRITING_GUIDE_URL = "https://docs.verbeo.ai/guides/knowledge-base-writing";

/** Where one file is in its upload. */
type Step =
  | { step: "preparing" }
  | { step: "uploading"; percent: number }
  | { step: "finishing" }
  | { step: "done" }
  | { step: "failed"; message: string };

interface Picked {
  key: number;
  file: File;
  /** The SHA-256 of its bytes; null for a file not worth reading (refused for its name, type or size);
   * undefined while it is read. */
  hash?: string | null;
}

interface UploadDialogProps {
  storeId: string;
  workspaceId: string;
  /** The knowledge base's files now. */
  files: KnowledgeBaseFile[];
  /** The plan's limits and what is used of them; undefined while they load. */
  usage: KnowledgeBaseUsage | undefined;
  /** Replace: the ready file one new file takes the place of. */
  replacing?: KnowledgeBaseFile;
  onClose: () => void;
  /** Percent sent of a file, by its id once it has one; null once its upload has settled. */
  onProgress: (fileId: string, percent: number | null) => void;
}

/**
 * Picks files and uploads them one after another (design §5, step 3 §5.3-5.7). Each file is checked against
 * the plan before any request, in the server's order — its type, its size, the same bytes already in this
 * knowledge base (read when it is picked), the files left in it, the storage and the text left — and a
 * refused one says why and is not sent. The rest go through `uploadFile`; a refusal there shows on that file
 * and the others carry on. The dialog stays open while a file is on its way. With `replacing` it takes one
 * file, which needs no room among the files and goes in that file's place once it is ready.
 */
export function UploadDialog({ storeId, workspaceId, files, usage, replacing, onClose, onProgress }: UploadDialogProps) {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [steps, setSteps] = useState<Record<number, Step>>({});
  const [running, setRunning] = useState(false);
  const nextKey = useRef(0);
  const refresh = useRefreshFiles(storeId, workspaceId);
  const { toast } = useToast();

  // Checked again on every render, against the files and usage as they now are: removing a file makes
  // room for the next. A file already sent is counted by the server's list, not here.
  const room = usage && uploadRoom(usage, files, replacing);
  const waiting = picked.filter((entry) => !steps[entry.key]);
  const reading = waiting.some((entry) => entry.hash === undefined);
  const refusals = room
    ? checkFiles(waiting.map(({ file, hash }) => ({ name: file.name, size: file.size, hash: hash ?? null })), room, files)
    : [];
  const refusalOf = new Map(waiting.map((entry, i) => [entry.key, refusals[i]]));
  const accepted = waiting.filter(
    (entry): entry is Picked & { hash: string } => typeof entry.hash === "string" && !refusalOf.get(entry.key),
  );

  const setStep = (key: number, step: Step) => setSteps((all) => ({ ...all, [key]: step }));

  const pick = (chosen: File[]) => {
    const added = chosen.map((file) => ({ key: nextKey.current++, file }));
    // A replacement is one file: picking again changes it.
    setPicked(replacing ? added : [...waiting, ...added]);
    setSteps({});
    // The bytes are read for the duplicate check; a file refused before its bytes matter is not read.
    for (const { key, file } of added) {
      const read = room && needsHash(file, room) ? sha256Hex(file) : Promise.resolve(null);
      read.then(
        (hash) => setPicked((all) => all.map((entry) => (entry.key === key ? { ...entry, hash } : entry))),
        () => setStep(key, { step: "failed", message: UNREADABLE }),
      );
    }
  };

  const start = async () => {
    setRunning(true);
    let failures = 0;
    for (const { key, file, hash } of accepted) {
      setStep(key, { step: "preparing" });
      let fileId: string | undefined;
      try {
        await uploadFile(
          storeId,
          file,
          hash,
          (id, percent) => {
            // Its slot exists now: the files table shows it.
            if (fileId === undefined) void refresh();
            fileId = id;
            onProgress(id, percent);
            setStep(key, percent < 100 ? { step: "uploading", percent } : { step: "finishing" });
          },
          replacing?.id,
        );
        setStep(key, { step: "done" });
      } catch (error) {
        failures += 1;
        setStep(key, { step: "failed", message: failureMessage(error, UPLOAD_FAILED) });
      }
      if (fileId !== undefined) onProgress(fileId, null);
    }
    await refresh();
    setRunning(false);
    if (failures === 0) {
      toast({ title: "Upload complete", description: `${fileCount(accepted.length)} uploaded and being processed.` });
      onClose();
    }
  };

  const status = (entry: Picked) => {
    const step = steps[entry.key];
    const refusal = refusalOf.get(entry.key);
    if (!step && entry.hash === undefined) return <p className="text-xs text-muted-foreground">Checking…</p>;
    if (refusal) return <p className="text-xs text-destructive">{refusal}</p>;
    switch (step?.step) {
      case "preparing":
        return <p className="text-xs text-muted-foreground">Preparing…</p>;
      case "uploading":
        return (
          <div className="space-y-1">
            <Progress value={step.percent} aria-label={`${entry.file.name} uploaded`} className="h-2" />
            <p className="text-xs text-muted-foreground">Uploading {step.percent}%</p>
          </div>
        );
      case "finishing":
        return <p className="text-xs text-muted-foreground">Finishing…</p>;
      case "done":
        return <p className="text-xs text-muted-foreground">Uploaded. It is being processed.</p>;
      case "failed":
        return <p className="text-xs text-destructive">{step.message}</p>;
      default:
        return <p className="text-xs text-muted-foreground">{running ? "Waiting…" : "Ready to upload"}</p>;
    }
  };

  return (
    <Dialog open onOpenChange={() => !running && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{replacing ? `Replace ${replacing.display_name}` : "Upload files"}</DialogTitle>
          <DialogDescription>
            {replacing
              ? `The new file is used once it is ready. Until then, your agents keep using ${replacing.display_name}.`
              : "PDF, Word (DOCX or DOC), ODT, RTF, EPUB, TXT, Markdown, HTML, XML, JSON, YAML, Excel (XLSX) or CSV."}
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          Short sections under clear headings, each question right above its answer, and one fact per table row
          answer best.{" "}
          <a href={WRITING_GUIDE_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2">
            Writing guide
          </a>
        </p>

        {room ? (
          <p className="text-sm text-muted-foreground">
            {room.files !== null &&
              `${fileCount(Math.max(room.limits.max_files_per_kb - room.files, 0))} left in this knowledge base, `}
            {formatBytes(Math.max(room.limits.max_storage_bytes - room.storageBytes, 0))} of storage left.
          </p>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Loading your plan's limits…
          </p>
        )}

        <div className="space-y-1">
          <Label htmlFor="kb-files">{replacing ? "Choose a file" : "Choose files"}</Label>
          <Input
            id="kb-files"
            type="file"
            multiple={!replacing}
            accept={ACCEPTED_EXTENSIONS}
            disabled={!room || running}
            aria-describedby={room ? "kb-files-limits" : undefined}
            onChange={(event) => {
              pick(Array.from(event.target.files));
              // The same file can be chosen again.
              event.target.value = "";
            }}
            className="cursor-pointer"
          />
          {room && (
            <p id="kb-files-limits" className="text-xs text-muted-foreground">
              {fileLimits(room.limits)}
            </p>
          )}
        </div>

        {picked.length > 0 && (
          <ul aria-label="Files to upload" className="max-h-64 space-y-2 overflow-y-auto">
            {picked.map((entry) => (
              <li key={entry.key} data-testid="kb-upload" className="flex items-start justify-between gap-3 rounded-md border p-2">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="truncate text-sm font-medium" title={entry.file.name}>
                    {entry.file.name}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">{formatBytes(entry.file.size)}</span>
                  </p>
                  {status(entry)}
                </div>
                {!running && !steps[entry.key] && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label={`Remove ${entry.file.name}`}
                    onClick={() => setPicked((all) => all.filter((other) => other.key !== entry.key))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button onClick={start} disabled={running || reading || accepted.length === 0} aria-busy={running}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {running ? "Uploading…" : `Upload ${fileCount(accepted.length)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
